/**
 * Atlas PM · 自动创建任务 · 项目侧接口契约（P8.29 重塑：选模板 + 当场设周期）
 *
 * 后端为 TaskAutoController（apps/api/src/modules/task-auto/task-auto.controller.ts）：
 *   GET    /task-auto/projects/:projectId/tasks      项目已配置自动任务 + 最近一次执行
 *   POST   /task-auto/projects/:projectId/tasks      { items } → 逐条 SUCCESS/SKIPPED/FAILED
 *   PATCH  /task-auto/projects/:projectId/tasks/:id  只改配置（周期 / 开关），不回溯补建
 *   DELETE /task-auto/projects/:projectId/tasks/:id  删除配置（不删已建任务）
 *
 * 任务模板列表（TaskTemplateController）：
 *   GET    /task-templates     默认只返回 isActive=true 的模板（个人 + 公共）
 *
 * 周期取值 WEEKLY | MONTHLY | QUARTERLY | YEARLY：
 *   cycleMonth（P8.30）—— QUARTERLY 1-3（季内第几月）／ YEARLY 1-12（几月）；
 *                           WEEKLY / MONTHLY 必须为 null。
 *   cycleDay —— WEEKLY 1-7（周一=1）；其余 0（该月最后一天）或 1-28
 *               （29-31 导致部分月份无该日，后端 DTO 直接拒绝并可透传原文）。
 */

import { api } from '../../api/client';
import type { Paginated } from '../../api/types';
import type { TaskTemplateItem } from '../settings/task-templates/api';

/** 周期类型：WEEKLY=每周（cycleDay 1..7，周一=1）/ 其余周期 1..28 */
export type AutoCycleType = 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export type AutoRunStatus = 'SUCCESS' | 'SKIPPED' | 'FAILED';

/** 单条自动任务配置入参（建项目 autoTasks / 项目详情 POST items 共用） */
export interface AutoTaskInput {
  templateId: number;
  cycleType: AutoCycleType;
  /** QUARTERLY 1-3 ／ YEARLY 1-12；WEEKLY / MONTHLY 传 null */
  cycleMonth?: number | null;
  cycleDay: number;
  durationDays?: number;
  leadDays?: number;
}

/** GET /projects/:projectId/tasks · 项目已配置的自动任务条目 */
export interface ProjectAutoTaskItem {
  id: string;
  templateId: string;
  templateName: string | null;
  cycleType: AutoCycleType;
  cycleMonth: number | null;
  cycleDay: number;
  durationDays: number;
  leadDays: number;
  cycleText: string;
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRun: {
    periodKey: string;
    status: string;
    message: string | null;
    createdAt: string;
  } | null;
}

/** POST /projects/:projectId/tasks · 逐条保存结果 */
export interface SaveProjectAutoTaskResult {
  templateId: string;
  status: AutoRunStatus;
  taskId: string | null;
  message: string | null;
}

export const AUTO_RUN_STATUS_LABEL: Record<AutoRunStatus, string> = {
  SUCCESS: '成功',
  SKIPPED: '已跳过',
  FAILED: '失败',
};

export const CYCLE_TYPE_LABEL: Record<AutoCycleType, string> = {
  WEEKLY: '每周',
  MONTHLY: '每月',
  QUARTERLY: '每季',
  YEARLY: '每年',
};

const BASE = '/task-auto';

export const taskAutoApi = {
  /** 可用任务模板（默认仅 isActive=true；一页取满 100 条上限） */
  templates: () => api.get<Paginated<TaskTemplateItem>>('/task-templates?pageSize=100'),

  /** 项目已配置的自动任务 + 最近一次执行记录 */
  projectTasks: (projectId: string) =>
    api.get<ProjectAutoTaskItem[]>(`${BASE}/projects/${projectId}/tasks`),

  /** 为项目新增/更新自动任务配置并立即按当前周期创建一次，返回逐条结果 */
  saveProjectTasks: (projectId: string, items: AutoTaskInput[]) =>
    api.post<SaveProjectAutoTaskResult[]>(`${BASE}/projects/${projectId}/tasks`, { items }),

  /** 只改自动任务配置（周期 / 开关），不回溯补建历史任务 */
  updateProjectTask: (
    projectId: string,
    id: string,
    body: {
      cycleType?: AutoCycleType;
      cycleMonth?: number | null;
      cycleDay?: number;
      durationDays?: number;
      leadDays?: number;
      isEnabled?: boolean;
    },
  ) => api.patch<{ ok: true }>(`${BASE}/projects/${projectId}/tasks/${id}`, body),

  /** 删除自动任务配置（不删已建任务） */
  removeProjectTask: (projectId: string, id: string) =>
    api.delete<{ ok: true; id: string }>(`${BASE}/projects/${projectId}/tasks/${id}`),
};

/* ── 周期展示 / 下拉选项（前端自绘，对齐后端 period.ts） ── */

const WEEK_NAMES = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'] as const;

/** 周期中文描述（对齐后端周期后端展示口径，如「每月5日」「每季度首月1日」） */
export function cycleTextOf(
  cycleType: AutoCycleType,
  cycleDay: number,
  cycleMonth?: number | null,
): string {
  switch (cycleType) {
    case 'WEEKLY':
      return `每周${WEEK_NAMES[cycleDay - 1] ?? ''}`;
    case 'MONTHLY':
      return cycleDay === 0 ? '每月最后一天' : `每月${cycleDay}日`;
    case 'QUARTERLY':
      return `每季第${cycleMonth ?? 1}月${cycleDay === 0 ? '最后一天' : `${cycleDay}日`}`;
    case 'YEARLY':
      return `每年${cycleMonth ?? 1}月${cycleDay === 0 ? '最后一天' : `${cycleDay}日`}`;
  }
}

/** 周期日选项：每周 = 周一..周日；其余 = [1-28 号, 0=最后一天] */
export function cycleDayOptions(cycleType: AutoCycleType): Array<{ value: number; label: string }> {
  if (cycleType === 'WEEKLY') {
    return WEEK_NAMES.map((name, i) => ({ value: i + 1, label: name }));
  }
  return [
    ...Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: `${i + 1}号` })),
    { value: 0, label: '最后一天' },
  ];
}

/** 周期月份选项：每季 = 第1/2/3月；每年 = 1月..12月；每周/每月 = 空 */
export function cycleMonthOptions(
  cycleType: AutoCycleType,
): Array<{ value: number; label: string }> {
  if (cycleType === 'QUARTERLY') {
    return [
      { value: 1, label: '第1月' },
      { value: 2, label: '第2月' },
      { value: 3, label: '第3月' },
    ];
  }
  if (cycleType === 'YEARLY') {
    return Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `${i + 1}月` }));
  }
  return [];
}

/** 切换周期类型后钳制执行日到合法区间（防残留无效值；0=月末 视为合法） */
export function clampCycleDay(cycleType: AutoCycleType, day: number): number {
  if (cycleType === 'WEEKLY') return day >= 1 && day <= 7 ? day : 1;
  return day === 0 || (day >= 1 && day <= 28) ? day : 5;
}

/** 切换周期类型后钳制周期月份：每季 1-3 ／ 每年 1-12，越界或缺失取 1；每周/每月 → null */
export function clampCycleMonth(
  cycleType: AutoCycleType,
  month: number | null | undefined,
): number | null {
  if (cycleType === 'QUARTERLY') {
    return month != null && Number.isInteger(month) && month >= 1 && month <= 3 ? month : 1;
  }
  if (cycleType === 'YEARLY') {
    return month != null && Number.isInteger(month) && month >= 1 && month <= 12 ? month : 1;
  }
  return null;
}

/** 前端提交前校验：与后端 DTO/服务层口径完全一致（WEEKLY/MONTHLY cycleMonth 必须为空等） */
export function isValidCycle(
  cycleType: AutoCycleType,
  cycleMonth: number | null | undefined,
  cycleDay: number,
): boolean {
  const dayOk =
    Number.isInteger(cycleDay) &&
    (cycleType === 'WEEKLY'
      ? cycleDay >= 1 && cycleDay <= 7
      : cycleDay === 0 || (cycleDay >= 1 && cycleDay <= 28));
  if (!dayOk) return false;
  switch (cycleType) {
    case 'WEEKLY':
    case 'MONTHLY':
      return cycleMonth === null || cycleMonth === undefined;
    case 'QUARTERLY':
      return (
        typeof cycleMonth === 'number' &&
        Number.isInteger(cycleMonth) &&
        cycleMonth >= 1 &&
        cycleMonth <= 3
      );
    case 'YEARLY':
      return (
        typeof cycleMonth === 'number' &&
        Number.isInteger(cycleMonth) &&
        cycleMonth >= 1 &&
        cycleMonth <= 12
      );
  }
}

/** 周期实时预览文案（P8.31 加分项；纯展示、不做复杂日历推演） */
export function cyclePreviewText(
  cycleType: AutoCycleType,
  cycleMonth: number | null | undefined,
  cycleDay: number,
): string {
  switch (cycleType) {
    case 'WEEKLY':
      return `每周${WEEK_NAMES[cycleDay - 1] ?? ''}`;
    case 'MONTHLY':
      return cycleDay === 0 ? '每月最后一天' : `每月${cycleDay}日`;
    case 'QUARTERLY': {
      const qm = cycleMonth ?? 1;
      const months = [qm, qm + 3, qm + 6, qm + 9];
      if (cycleDay === 0) {
        return `每年 ${months.map((m) => `${m}月最后一天`).join('、')}`;
      }
      return `每年 ${months.map((m) => `${m}月${cycleDay}`).join('、')}`;
    }
    case 'YEARLY': {
      const ym = cycleMonth ?? 1;
      if (cycleDay === 0) {
        return ym === 2 ? '每年 2月最后一天（闰年 2月29日）' : `每年 ${ym}月最后一天`;
      }
      return `每年 ${ym}月${cycleDay}日`;
    }
  }
}